/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React from "react";
import { IUserSubprofileModel, IUserSubprofilesStateModel } from "@nf/common";
import { ROUTES } from "@nf/constants";
import { LinkWithLocale } from "components/LinkWithLocale";
import { useTranslation } from "next-i18next";
import cx from "classnames";
import AddCircleIcon from "../../../resources/icons/add-profile-circle.svg";
import EditIcon from "../../../resources/icons/edit-circle.svg";
import styles from "./ProfileCircle.module.scss";

export interface IProfileCircleProps {
  subprofile?: IUserSubprofileModel;
  editButton?: boolean;
  size?: "medium" | "small";
  className?: string;
  ellipseText?: boolean;
  trimUsername?: boolean;
  selectProfile?: (subprofile?: IUserSubprofileModel) => void;
}

export interface IAddProfileProps {
  subprofilesState?: IUserSubprofilesStateModel;
}

export const ProfileCircle = ({
  subprofile,
  editButton = true,
  size = "medium",
  className,
  ellipseText,
  trimUsername,
  selectProfile,
}: IProfileCircleProps) => {
  const displayName = trimUsername
    ? subprofile?.DisplayName?.split(" ")[0]
    : subprofile?.DisplayName;

  return (
    <div className={cx(styles.profile, styles[size], className)}>
      <div
        className={styles.circle}
        style={{ backgroundColor: subprofile?.Color }}
        onClick={() => selectProfile && selectProfile(subprofile)}
      >
        <span className={styles.letter}>{subprofile?.FirstLetter}</span>
        {editButton && (
          <div className={styles.editButton}>
            <LinkWithLocale
              href={`${ROUTES.SETTINGS_EDIT_SUBRPOFILE}/${subprofile?.UserId}`}
            >
              <a>
                <EditIcon className={styles.editIcon} />
              </a>
            </LinkWithLocale>
          </div>
        )}
      </div>
      <div
        className={cx(styles.title, {
          [styles.titleOverflow]: ellipseText,
        })}
      >
        {displayName}
      </div>
    </div>
  );
};

export const AddProfile = ({ subprofilesState }: IAddProfileProps) => {
  const { t } = useTranslation("translations");

  if (
    (subprofilesState?.currentSubprofile &&
      !subprofilesState.currentSubprofile.Owner) ||
    (subprofilesState?.subprofiles?.length ?? 0) > 4
  )
    return null;

  return (
    <div className={styles.profile}>
      <div className={styles.addButton}>
        <LinkWithLocale href={ROUTES.SETTINGS_ADD_SUBRPOFILE}>
          <a>
            <AddCircleIcon className={styles.addIcon} />
          </a>
        </LinkWithLocale>
      </div>
      <div className={styles.title}>
        {t("web_subprofiles_button_add_profile")}
      </div>
    </div>
  );
};
